import React, { useEffect, useMemo, useState, useRef } from 'react';
import Swal from '../librarianSwal';
import { useAuth } from '../../Constant/Auth';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getFinesByLibrary,
  getMembersByLibrary,
  getIssuesByLibrary,
  addFine,
  updateFine,
  getFineRulesByLibrary,
  addFineRule,
  updateFineRule,
  getErrorMessage
} from '../../api/api';
import { readPrefetched } from '../../api/prefetchCache';
import { exportToCSV } from '../exportUtils';
import '../lib-page-header.css';
import '../lib-typography.css';
import './FineList.css';
import Pagination from '../components/Pagination';

const FINES_RESOURCE = 'fines';
const PAGE_SIZE = 10;

/* Custom filter dropdown — replaces the native <select> so the menu stays
   aligned/contained on mobile instead of overflowing the card. */
const FmSelect = ({ value, options, placeholder = 'Select', onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="fm-dropdown" ref={ref}>
      <button
        type="button"
        className={`fm-dropdown-btn ${value ? 'active' : ''}`}
        onClick={() => setOpen((p) => !p)}
      >
        <span className="fm-dropdown-label">{selected ? selected.label : placeholder}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} />
      </button>

      {open && (
        <div className="fm-dropdown-menu">
          {options.map((o) => (
            <div
              key={o.value}
              className={`fm-dropdown-item ${value === o.value ? 'selected' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const initialFineForm = {
  membership_id: '',
  member_id: '',
  issue_id: '',
  fine_type: 'late',
  amount: '',
  reason: '',
  remarks: '',
  auto_note: ''
};

const initialRuleForm = {
  member_source: 'school_parent',
  late_fine_per_day: '',
  grace_days: 0,
  lost_book_charge_type: 'fixed',
  lost_book_charge: '',
  damaged_book_charge_type: 'fixed',
  damaged_book_charge: '',
  status: 'active'
};

const OPEN_FINE_STATUS = 'unpaid';

const FINE_TYPE_OPTIONS = [
  { value: 'late', label: 'Late' },
  { value: 'damage', label: 'Damage' },
  { value: 'lost', label: 'Lost' },
  { value: 'other', label: 'Other' }
];

const RULE_MEMBER_SOURCE_OPTIONS = [
  { value: 'school_parent', label: 'Parent' },
  { value: 'outsider', label: 'Outsider' },
  { value: 'staff', label: 'Staff' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'other', label: 'Other' }
];

const CHARGE_TYPE_OPTIONS = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'book_price', label: 'Book Price' }
];

const RULE_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const money = (value) => `₹ ${Number(value || 0).toLocaleString('en-IN')}`;

const formatDate = (value) => {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return String(value).slice(0, 10);
  }
};

const getMemberName = (item) => {
  return (
    item?.member_name ||
    item?.parent_name ||
    item?.full_name ||
    `${item?.first_name || ''} ${item?.last_name || ''}`.trim() ||
    '-'
  );
};

const getBookName = (item) => {
  return item?.book_title || item?.title || item?.book_name || '-';
};

const normalizeStatus = (status) => {
  const value = String(status || '').toLowerCase();

  if (value === 'pending') return 'unpaid';
  if (value === 'partially_paid') return 'partial';

  return value || '-';
};

const getStatusLabel = (status) => {
  const value = normalizeStatus(status);

  if (value === 'unpaid') return 'Unpaid';
  if (value === 'partial') return 'Partial';
  if (value === 'paid') return 'Paid';
  if (value === 'waived') return 'Waived';
  if (value === 'active') return 'Active';
  if (value === 'inactive') return 'Inactive';

  return value;
};

const getStatusBadgeClass = (status) => {
  const value = normalizeStatus(status);

  if (value === 'paid') return 'fm-badge fm-badge-paid';
  if (value === 'unpaid') return 'fm-badge fm-badge-unpaid';
  if (value === 'partial') return 'fm-badge fm-badge-partial';
  if (value === 'waived') return 'fm-badge fm-badge-waived';
  if (value === 'active') return 'fm-badge fm-badge-active';
  if (value === 'inactive') return 'fm-badge fm-badge-inactive';

  return 'fm-badge';
};

const startOfDay = (dateValue) => {
  const date = new Date(dateValue);
  date.setHours(0, 0, 0, 0);
  return date;
};

const calculateLateDays = (dueDate, graceDays = 0) => {
  if (!dueDate) return 0;

  const due = startOfDay(dueDate);
  const today = startOfDay(new Date());

  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.ceil(diffTime / 86400000);
  const lateDays = diffDays - Number(graceDays || 0);

  return lateDays > 0 ? lateDays : 0;
};

const getIssueMemberSource = (issue) => {
  const raw = String(
    issue?.member_source ||
      issue?.membership_type ||
      issue?.member_type ||
      ''
  ).toLowerCase();

  if (raw === 'school_parent' || raw === 'parent' || raw === 'student') {
    return 'school_parent';
  }

  if (raw === 'outsider') return 'outsider';
  if (raw === 'staff') return 'staff';
  if (raw === 'teacher') return 'teacher';

  if (issue?.student_id || issue?.student_name) return 'school_parent';

  return raw || 'outsider';
};

const getBookBasePrice = (issue) => {
  return Number(
    issue?.book_price ||
      issue?.price ||
      issue?.mrp ||
      issue?.purchase_price ||
      issue?.replacement_cost ||
      0
  );
};

const calculateCharge = (chargeType, chargeValue, issue) => {
  const type = String(chargeType || 'fixed').toLowerCase();
  const value = Number(chargeValue || 0);
  const basePrice = getBookBasePrice(issue);

  if (type === 'percentage') {
    return basePrice > 0 ? (basePrice * value) / 100 : 0;
  }

  if (type === 'book_price') {
    return basePrice;
  }

  return value;
};

const formatSource = (value) => {
  const source = String(value || '-').replace(/_/g, ' ');
  return source.charAt(0).toUpperCase() + source.slice(1);
};

const showToast = (icon, title) => {
  return Swal.fire({
    toast: true,
    position: 'top-end',
    icon,
    title,
    timer: 2200,
    timerProgressBar: true,
    showConfirmButton: false
  });
};

const showMessage = (icon, title, text) => {
  return Swal.fire({
    icon,
    title,
    text,
    timer: 2600,
    showConfirmButton: false
  });
};

const FineList = () => {
  const { user } = useAuth();

  const libraryId = useActiveLibraryId();
  const staffId = user?.staff_id || user?.staff_user_id || user?.staffUserId || null;

  const [fines, setFines] = useState([]);
  const [rules, setRules] = useState([]);
  const [members, setMembers] = useState([]);
  const [issues, setIssues] = useState([]);

  const [loading, setLoading] = useState(false);
  const [savingFine, setSavingFine] = useState(false);
  const [savingRule, setSavingRule] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const [showFineModal, setShowFineModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);

  const [fineForm, setFineForm] = useState(initialFineForm);
  const [ruleForm, setRuleForm] = useState(initialRuleForm);
  const [editingRuleId, setEditingRuleId] = useState(null);

  const fetchData = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);

      const [fineRes, ruleRes, memberRes, issueRes] = await Promise.all([
        getFinesByLibrary(libraryId),
        getFineRulesByLibrary(libraryId),
        getMembersByLibrary(libraryId),
        getIssuesByLibrary(libraryId)
      ]);

      setFines(toArray(fineRes));
      setRules(toArray(ruleRes));
      setMembers(toArray(memberRes));
      setIssues(toArray(issueRes));
    } catch (error) {
      showToast('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Seed instantly from a hover-triggered prefetch if warm, then always
    // still run the real fetch — fine/balance figures are never trusted
    // from a cache older than this.
    const warmFines = readPrefetched(FINES_RESOURCE, libraryId);
    if (warmFines) setFines(toArray(warmFines));

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const getMatchingRule = (issue) => {
    if (!issue) return null;

    const source = getIssueMemberSource(issue);
    const possibleSources = [source];

    if (source === 'school_parent') {
      possibleSources.push('student', 'parent');
    }

    const activeRules = rules.filter(
      (rule) => String(rule.status || 'active').toLowerCase() === 'active'
    );

    return (
      activeRules.find((rule) =>
        possibleSources.includes(String(rule.member_source || '').toLowerCase())
      ) || null
    );
  };

  const calculateAutoFine = (issue, fineType) => {
    const type = String(fineType || '').toLowerCase();

    if (type === 'other') {
      return {
        amount: '',
        reason: 'Other fine',
        note: 'Manual fine amount'
      };
    }

    if (!issue) {
      return {
        amount: '',
        reason: '',
        note: ''
      };
    }

    const rule = getMatchingRule(issue);

    if (!rule) {
      return {
        amount: '',
        reason: '',
        note: 'No active fine rule found for this member type'
      };
    }

    if (type === 'late') {
      const lateDays = calculateLateDays(issue.due_date, rule.grace_days || 0);
      const perDay = Number(rule.late_fine_per_day || 0);
      const amount = lateDays * perDay;

      return {
        amount,
        reason:
          lateDays > 0
            ? `Late return fine for ${lateDays} day(s)`
            : 'No late fine applicable',
        note: `Rule: ${lateDays} day(s) × ${money(perDay)}`
      };
    }

    if (type === 'damage') {
      const amount = calculateCharge(
        rule.damaged_book_charge_type,
        rule.damaged_book_charge,
        issue
      );

      return {
        amount,
        reason: 'Book damage fine',
        note: `Rule: Damage charge ${money(amount)}`
      };
    }

    if (type === 'lost') {
      const amount = calculateCharge(
        rule.lost_book_charge_type,
        rule.lost_book_charge,
        issue
      );

      return {
        amount,
        reason: 'Lost book fine',
        note: `Rule: Lost book charge ${money(amount)}`
      };
    }

    return {
      amount: '',
      reason: '',
      note: ''
    };
  };

  const stats = useMemo(() => {
    const totalAmount = fines.reduce(
      (sum, fine) => sum + Number(fine.amount || fine.fine_amount || 0),
      0
    );

    const balanceAmount = fines.reduce(
      (sum, fine) => sum + Number(fine.balance_amount || 0),
      0
    );

    const openCount = fines.filter((fine) => {
      const status = normalizeStatus(fine.fine_status || fine.status);
      return status === 'unpaid' || status === 'partial';
    }).length;

    const paidCount = fines.filter((fine) => {
      const status = normalizeStatus(fine.fine_status || fine.status);
      return status === 'paid';
    }).length;

    return {
      totalFines: fines.length,
      totalAmount,
      balanceAmount,
      openCount,
      paidCount,
      totalRules: rules.length
    };
  }, [fines, rules]);

  const filteredFines = useMemo(() => {
    const keyword = search.toLowerCase().trim();

    return fines.filter((fine) => {
      const status = normalizeStatus(fine.fine_status || fine.status);

      const matchSearch =
        !keyword ||
        String(getMemberName(fine)).toLowerCase().includes(keyword) ||
        String(getBookName(fine)).toLowerCase().includes(keyword) ||
        String(fine.accession_no || '').toLowerCase().includes(keyword) ||
        String(fine.barcode || '').toLowerCase().includes(keyword) ||
        String(fine.reason || fine.remarks || '').toLowerCase().includes(keyword) ||
        String(fine.fine_type || '').toLowerCase().includes(keyword);

      const matchStatus = statusFilter ? status === statusFilter : true;

      return matchSearch && matchStatus;
    });
  }, [fines, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredFines.length / PAGE_SIZE));
  const paginatedFines = filteredFines.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const fineExportColumns = [
    { header: 'Member/Book', key: '__member' },
    { header: 'Type', key: 'fine_type' },
    { header: 'Amount', key: '__amount' },
    { header: 'Balance', key: 'balance_amount' },
    { header: 'Status', key: '__status' },
    { header: 'Reason', key: 'reason' }
  ];

  const fineExportRows = filteredFines.map((fine) => ({
    ...fine,
    __member: `${getMemberName(fine)} - ${getBookName(fine)}`,
    __amount: fine.amount || fine.fine_amount || 0,
    __status: getStatusLabel(fine.fine_status || fine.status)
  }));

  const handleExportFinesCSV = () => {
    exportToCSV(fineExportColumns, fineExportRows, 'fines_list');
  };

  const openFineModal = () => {
    setFineForm(initialFineForm);
    setShowFineModal(true);
  };

  const closeFineModal = () => {
    if (savingFine) return;
    setShowFineModal(false);
    setFineForm(initialFineForm);
  };

  const openRuleModal = (rule = null) => {
    if (rule) {
      setEditingRuleId(rule.id);
      setRuleForm({
        member_source: rule.member_source || 'school_parent',
        late_fine_per_day: rule.late_fine_per_day || '',
        grace_days: rule.grace_days || 0,
        lost_book_charge_type: rule.lost_book_charge_type || 'fixed',
        lost_book_charge: rule.lost_book_charge || '',
        damaged_book_charge_type: rule.damaged_book_charge_type || 'fixed',
        damaged_book_charge: rule.damaged_book_charge || '',
        status: rule.status || 'active'
      });
    } else {
      setEditingRuleId(null);
      setRuleForm(initialRuleForm);
    }

    setShowRuleModal(true);
  };

  const closeRuleModal = () => {
    if (savingRule) return;
    setShowRuleModal(false);
    setEditingRuleId(null);
    setRuleForm(initialRuleForm);
  };

  const handleFineChange = (e) => {
    const { name, value } = e.target;

    if (name === 'issue_id') {
      const selectedIssue = issues.find(
        (item) => String(item.id) === String(value)
      );

      if (!value) {
        setFineForm((prev) => ({
          ...prev,
          issue_id: '',
          member_id: '',
          membership_id: '',
          amount: '',
          reason: '',
          auto_note: ''
        }));
        return;
      }

      const autoFine = calculateAutoFine(selectedIssue, fineForm.fine_type);

      setFineForm((prev) => ({
        ...prev,
        issue_id: value,
        member_id: selectedIssue?.member_id || '',
        membership_id: selectedIssue?.membership_id || '',
        amount:
          autoFine.amount !== '' && autoFine.amount !== null
            ? Number(autoFine.amount)
            : '',
        reason: autoFine.reason || prev.reason,
        auto_note: autoFine.note || ''
      }));

      return;
    }

    if (name === 'fine_type') {
      const selectedIssue = issues.find(
        (item) => String(item.id) === String(fineForm.issue_id)
      );

      const autoFine = calculateAutoFine(selectedIssue, value);

      setFineForm((prev) => ({
        ...prev,
        fine_type: value,
        amount:
          autoFine.amount !== '' && autoFine.amount !== null
            ? Number(autoFine.amount)
            : '',
        reason: autoFine.reason || '',
        auto_note: autoFine.note || ''
      }));

      return;
    }

    if (name === 'member_id') {
      const selectedMember = members.find(
        (item) => String(item.id) === String(value)
      );

      setFineForm((prev) => ({
        ...prev,
        member_id: value,
        membership_id:
          selectedMember?.membership_id ||
          selectedMember?.active_membership_id ||
          selectedMember?.membershipId ||
          prev.membership_id
      }));

      return;
    }

    setFineForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const getFinalFineIds = () => {
    const selectedIssue = issues.find(
      (item) => String(item.id) === String(fineForm.issue_id)
    );

    const selectedMember = members.find(
      (item) => String(item.id) === String(fineForm.member_id)
    );

    const finalMembershipId =
      fineForm.membership_id ||
      selectedIssue?.membership_id ||
      selectedMember?.membership_id ||
      selectedMember?.active_membership_id ||
      selectedMember?.membershipId ||
      '';

    const finalMemberId =
      fineForm.member_id ||
      selectedIssue?.member_id ||
      '';

    return {
      selectedIssue,
      selectedMember,
      finalMembershipId,
      finalMemberId
    };
  };

  const validateFine = (finalMemberId, finalMembershipId) => {
    if (!libraryId) {
      return 'Library ID not found';
    }

    if (!finalMemberId || !finalMembershipId || !fineForm.fine_type || fineForm.amount === '') {
      return 'Member, membership, fine type and amount are required';
    }

    if (Number(fineForm.amount) <= 0) {
      if (fineForm.reason === 'No late fine applicable') {
        return 'No late fine is applicable for this issue. Select another issue/fine type or enter manual amount.';
      }

      return 'Amount should be greater than 0';
    }

    return '';
  };

  const handleSubmitFine = async (e) => {
    e.preventDefault();

    const { finalMembershipId, finalMemberId } = getFinalFineIds();

    const validationError = validateFine(finalMemberId, finalMembershipId);

    if (validationError) {
      showMessage('error', 'Error', validationError);
      return;
    }

    const payload = {
      library_id: Number(libraryId),
      membership_id: Number(finalMembershipId),
      member_id: Number(finalMemberId),
      issue_id: fineForm.issue_id ? Number(fineForm.issue_id) : null,
      fine_type: fineForm.fine_type,
      amount: Number(fineForm.amount),
      paid_amount: 0,
      balance_amount: Number(fineForm.amount),
      adjustment_source: 'manual',
      fine_status: OPEN_FINE_STATUS,
      reason: fineForm.reason || fineForm.remarks || 'Manual fine',
      imposed_by_staff_id: staffId || null,
      settled_at: null
    };

    try {
      setSavingFine(true);

      await addFine(payload);

      closeFineModal();
      await showToast('success', 'Fine created successfully');
      fetchData();
    } catch (error) {
      showMessage('error', 'Error', getErrorMessage(error));
    } finally {
      setSavingFine(false);
    }
  };

  const handleResetFineFilters = () => {
    setSearch('');
    setStatusFilter('');
  };

  const buildFineUpdatePayload = (fine, overrides = {}) => {
    const amount = Number(overrides.amount ?? fine.amount ?? fine.fine_amount ?? 0);
    const paidAmount = Number(overrides.paid_amount ?? fine.paid_amount ?? 0);
    const balanceAmount = Number(
      overrides.balance_amount ??
        fine.balance_amount ??
        Math.max(amount - paidAmount, 0)
    );

    return {
      library_id: Number(overrides.library_id ?? fine.library_id ?? libraryId),
      issue_id: overrides.issue_id ?? fine.issue_id ?? null,
      membership_id: Number(overrides.membership_id ?? fine.membership_id),
      member_id: Number(overrides.member_id ?? fine.member_id),
      fine_type: overrides.fine_type ?? fine.fine_type ?? 'other',
      amount,
      paid_amount: paidAmount,
      balance_amount: balanceAmount,
      adjustment_source: overrides.adjustment_source ?? fine.adjustment_source ?? 'manual',
      fine_status: overrides.fine_status ?? normalizeStatus(fine.fine_status || fine.status || OPEN_FINE_STATUS),
      reason: overrides.reason ?? fine.reason ?? null,
      imposed_by_staff_id: overrides.imposed_by_staff_id ?? fine.imposed_by_staff_id ?? staffId ?? null,
      settled_at: overrides.settled_at ?? fine.settled_at ?? null
    };
  };

  const handlePayFine = async (fine) => {
    const fineId = fine.id || fine.fine_id;
    const balance = Number(fine.balance_amount ?? fine.amount ?? 0);

    if (!fineId) {
      showMessage('error', 'Error', 'Fine ID not found');
      return;
    }

    if (balance <= 0) {
      showToast('info', 'No balance pending');
      return;
    }

    const { value: paidNow, isConfirmed } = await Swal.fire({
      title: 'Collect Payment',
      html: `<div style="text-align:left;font-size:13px;color:#475569;margin-bottom:8px;">
        ${getMemberName(fine)} - ${getBookName(fine)}<br/>
        Balance due: <b>${money(balance)}</b>
      </div>`,
      input: 'number',
      inputLabel: 'Amount received',
      inputValue: balance,
      inputAttributes: {
        min: 1,
        step: '0.01'
      },
      showCancelButton: true,
      confirmButtonText: 'Collect',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (value === '' || value === null) return 'Please enter an amount';
        if (Number(value) <= 0) return 'Amount must be greater than 0';
        if (Number(value) > balance) return `Amount cannot exceed balance of ${money(balance)}`;
        return null;
      }
    });

    if (!isConfirmed) return;

    const amountPaid = Number(paidNow);
    const newBalance = Math.max(balance - amountPaid, 0);
    const totalPaid = Number(fine.paid_amount || 0) + amountPaid;

    const payload = buildFineUpdatePayload(fine, {
      paid_amount: totalPaid,
      balance_amount: newBalance,
      fine_status: newBalance <= 0 ? 'paid' : 'partial',
      adjustment_source: 'payment',
      settled_at: newBalance <= 0 ? new Date().toISOString().slice(0, 19).replace('T', ' ') : null
    });

    try {
      await updateFine(fineId, payload);

      await showToast('success', 'Payment recorded successfully');
      fetchData();
    } catch (error) {
      showMessage('error', 'Error', getErrorMessage(error));
    }
  };

  const handleWaiveFine = async (fine) => {
    const fineId = fine.id || fine.fine_id;

    if (!fineId) {
      showMessage('error', 'Error', 'Fine ID not found');
      return;
    }

    const { value: reason, isConfirmed } = await Swal.fire({
      title: 'Waive Fine?',
      html: `<div style="text-align:left;font-size:13px;color:#475569;margin-bottom:8px;">
        ${getMemberName(fine)} - ${getBookName(fine)}<br/>
        Balance: <b>${money(fine.balance_amount ?? fine.amount)}</b>
      </div>`,
      input: 'textarea',
      inputLabel: 'Reason for waiving',
      inputPlaceholder: 'Enter reason...',
      showCancelButton: true,
      confirmButtonText: 'Waive Fine',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'Please enter a reason';
        return null;
      }
    });

    if (!isConfirmed) return;

    const waiveReason = reason.trim();
    const payload = buildFineUpdatePayload(fine, {
      balance_amount: 0,
      fine_status: 'waived',
      adjustment_source: 'waived',
      reason: fine.reason ? `${fine.reason} | Waived: ${waiveReason}` : `Waived: ${waiveReason}`,
      settled_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
    });

    try {
      await updateFine(fineId, payload);

      await showToast('success', 'Fine waived successfully');
      fetchData();
    } catch (error) {
      showMessage('error', 'Error', getErrorMessage(error));
    }
  };

  const handleRuleChange = (e) => {
    const { name, value } = e.target;

    setRuleForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const validateRule = () => {
    if (!libraryId) return 'Library ID not found';
    if (!ruleForm.member_source) return 'Member source is required';
    if (ruleForm.late_fine_per_day === '') return 'Late fine per day is required';
    if (Number(ruleForm.late_fine_per_day) < 0) return 'Late fine cannot be negative';
    if (Number(ruleForm.grace_days) < 0) return 'Grace days cannot be negative';
    if (Number(ruleForm.lost_book_charge || 0) < 0) return 'Lost book charge cannot be negative';
    if (Number(ruleForm.damaged_book_charge || 0) < 0) return 'Damage charge cannot be negative';

    return '';
  };

  const buildRulePayload = (statusValue = ruleForm.status) => ({
    library_id: Number(libraryId),
    member_source: String(ruleForm.member_source || '').trim(),
    late_fine_per_day: Number(ruleForm.late_fine_per_day || 0),
    grace_days: Number(ruleForm.grace_days || 0),
    lost_book_charge_type: ruleForm.lost_book_charge_type || 'fixed',
    lost_book_charge: Number(ruleForm.lost_book_charge || 0),
    damaged_book_charge_type: ruleForm.damaged_book_charge_type || 'fixed',
    damaged_book_charge: Number(ruleForm.damaged_book_charge || 0),
    status: statusValue || 'active'
  });

  const handleSubmitRule = async (e) => {
    e.preventDefault();

    const validationError = validateRule();

    if (validationError) {
      showMessage('error', 'Error', validationError);
      return;
    }

    try {
      setSavingRule(true);

      const payload = buildRulePayload();

      if (editingRuleId) {
        await updateFineRule(editingRuleId, payload);
        closeRuleModal();
        await showToast('success', 'Fine rule updated successfully');
      } else {
        await addFineRule(payload);
        closeRuleModal();
        await showToast('success', 'Fine rule added successfully');
      }

      fetchData();
    } catch (error) {
      showMessage('error', 'Error', getErrorMessage(error));
    } finally {
      setSavingRule(false);
    }
  };

  const handleRuleStatusChange = async (rule) => {
    try {
      const newStatus = rule.status === 'active' ? 'inactive' : 'active';

      const payload = {
        library_id: Number(rule.library_id || libraryId),
        member_source: rule.member_source,
        late_fine_per_day: Number(rule.late_fine_per_day || 0),
        grace_days: Number(rule.grace_days || 0),
        lost_book_charge_type: rule.lost_book_charge_type || 'fixed',
        lost_book_charge: Number(rule.lost_book_charge || 0),
        damaged_book_charge_type: rule.damaged_book_charge_type || 'fixed',
        damaged_book_charge: Number(rule.damaged_book_charge || 0),
        status: newStatus
      };

      await updateFineRule(rule.id, payload);

      await showToast('success', 'Rule status updated');
      fetchData();
    } catch (error) {
      showMessage('error', 'Error', getErrorMessage(error));
    }
  };

  return (
    <div className="container-fluid fm-page">

      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-receipt" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Fine Management</h3>
            <div className="lib-page-header__subtitle">Manage fine rules, member fines, payments and waivers.</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button type="button" className="fm-btn-light" onClick={fetchData} disabled={loading}>
            <i className="fas fa-sync-alt" />
            Refresh
          </button>

          <button type="button" className="fm-btn-light" onClick={handleExportFinesCSV}>
            <i className="fas fa-file-csv" />
            CSV
          </button>

          <button type="button" className="fm-btn-primary" onClick={openFineModal}>
            <i className="fas fa-plus" />
            Add Fine
          </button>
        </div>
      </div>

      <div className="fm-stat-grid ms-4">
        <div className="fm-stat-card" style={{ '--accent': '#2563eb' }}>
          <span className="fm-stat-icon">
            <i className="fas fa-receipt" />
          </span>
          <div className="fm-stat-label">Total Fines</div>
          <div className="fm-stat-value">{stats.totalFines}</div>
        </div>

        <div className="fm-stat-card" style={{ '--accent': '#7c3aed' }}>
          <span className="fm-stat-icon">
            <i className="fas fa-coins" />
          </span>
          <div className="fm-stat-label">Total Amount</div>
          <div className="fm-stat-value">{money(stats.totalAmount)}</div>
        </div>

        <div className="fm-stat-card" style={{ '--accent': '#16a34a' }}>
          <span className="fm-stat-icon">
            <i className="fas fa-wallet" />
          </span>
          <div className="fm-stat-label">Balance</div>
          <div className="fm-stat-value">{money(stats.balanceAmount)}</div>
        </div>

        <div className="fm-stat-card" style={{ '--accent': '#dc2626' }}>
          <span className="fm-stat-icon">
            <i className="fas fa-exclamation-triangle" />
          </span>
          <div className="fm-stat-label">Open Cases</div>
          <div className="fm-stat-value">{stats.openCount}</div>
        </div>

        <div className="fm-stat-card" style={{ '--accent': '#f59e0b' }}>
          <span className="fm-stat-icon">
            <i className="fas fa-sliders-h" />
          </span>
          <div className="fm-stat-label">Fine Rules</div>
          <div className="fm-stat-value">{stats.totalRules}</div>
        </div>
      </div>

      <div className="fm-section-card ms-4">
        <div className="fm-section-head">
          <h5 className="fm-section-title color-white">
            <i className="fas fa-list" />
            Fine List
          </h5>
        </div>

        <div className="fm-section-body">
          <div className="fm-filter-grid">
            <input
              type="text"
              className="fm-input"
              placeholder="Search by member, book, accession no, reason..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <FmSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: '', label: 'All Status' },
                { value: 'unpaid', label: 'Unpaid' },
                { value: 'partial', label: 'Partial' },
                { value: 'paid', label: 'Paid' },
                { value: 'waived', label: 'Waived' },
              ]}
            />

            <button type="button" className="fm-btn-light" onClick={handleResetFineFilters}>
              Reset
            </button>

            {/* <button type="button" className="fm-btn-primary" onClick={openFineModal}>
              Add Fine
            </button> */}
          </div>

          {loading ? (
            <div className="fm-loader">Loading fines...</div>
          ) : (
            <div className="fm-table-wrap">
              <table className="fm-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Member / Book</th>
                    <th className="fm-col-secondary">Type</th>
                    <th>Amount</th>
                    <th className="fm-col-secondary">Paid</th>
                    <th>Balance</th>
                    <th className="fm-col-secondary">Created On</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedFines.length > 0 ? (
                    paginatedFines.map((fine, index) => {
                      const status = normalizeStatus(fine.fine_status || fine.status);
                      const isClosed = status === 'paid' || status === 'waived';

                      return (
                        <tr key={fine.id || fine.fine_id || index}>
                          <td>{(page - 1) * PAGE_SIZE + index + 1}</td>

                          <td>
                            <div className="fm-main">{getMemberName(fine)}</div>
                            <div className="fm-sub">
                              {getBookName(fine)}
                              {fine.accession_no ? ` (${fine.accession_no})` : ''}
                            </div>
                            {fine.reason ? (
                              <div className="fm-sub">Reason: {fine.reason}</div>
                            ) : null}
                          </td>

                          <td className="text-capitalize fm-col-secondary">{fine.fine_type || '-'}</td>
                          <td>{money(fine.amount || fine.fine_amount)}</td>
                          <td className="fm-col-secondary">{money(fine.paid_amount)}</td>
                          <td>{money(fine.balance_amount)}</td>
                          <td className="fm-col-secondary">{formatDate(fine.created_at || fine.imposed_at)}</td>

                          <td>
                            <span className={getStatusBadgeClass(status)}>
                              {getStatusLabel(status)}
                            </span>
                          </td>

                          <td>
                            {isClosed ? (
                              <span className="fm-muted">No action</span>
                            ) : (
                              <div className="fm-actions">
                                <button
                                  type="button"
                                  className="fm-act fm-act-pay"
                                  onClick={() => handlePayFine(fine)}
                                >
                                  <i className="fas fa-rupee-sign" />
                                  Pay
                                </button>

                                <button
                                  type="button"
                                  className="fm-act fm-act-waive"
                                  onClick={() => handleWaiveFine(fine)}
                                >
                                  <i className="fas fa-ban" />
                                  Waive
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="fm-empty">
                        No fines found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={filteredFines.length}
                itemLabel="fines"
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>

      <div className="fm-section-card ms-4">
        <div className="fm-section-head">
          <h5 className="fm-section-title">
            <i className="fas fa-sliders-h" />
            Fine Rules
          </h5>

          <button type="button" className="fm-btn-light" onClick={() => openRuleModal()}>
            <i className="fas fa-plus" />
            Add Rule
          </button>
        </div>

        <div className="fm-section-body">
          {loading ? (
            <div className="fm-loader">Loading rules...</div>
          ) : (
            <div className="fm-table-wrap">
              <table className="fm-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Source</th>
                    <th>Late / Grace</th>
                    <th>Lost</th>
                    <th>Damage</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {rules.length > 0 ? (
                    rules.map((rule, index) => (
                      <tr key={rule.id || index}>
                        <td>{index + 1}</td>

                        <td>
                          <div className="fm-main">{formatSource(rule.member_source)}</div>
                        </td>

                        <td>
                          <div className="fm-main">{money(rule.late_fine_per_day)} / day</div>
                          <div className="fm-sub">Grace: {rule.grace_days || 0} days</div>
                        </td>

                        <td>
                          <div className="fm-main">{money(rule.lost_book_charge)}</div>
                          <div className="fm-sub">{formatSource(rule.lost_book_charge_type)}</div>
                        </td>

                        <td>
                          <div className="fm-main">{money(rule.damaged_book_charge)}</div>
                          <div className="fm-sub">{formatSource(rule.damaged_book_charge_type)}</div>
                        </td>

                        <td>
                          <span className={getStatusBadgeClass(rule.status)}>
                            {getStatusLabel(rule.status)}
                          </span>
                        </td>

                        <td>
                          <div className="fm-actions">
                            <button
                              type="button"
                              className="fm-act fm-act-edit"
                              onClick={() => openRuleModal(rule)}
                            >
                              <i className="fas fa-pen" />
                              Edit
                            </button>

                            <button
                              type="button"
                              className={`fm-act ${
                                rule.status === 'active'
                                  ? 'fm-act-toggle-off'
                                  : 'fm-act-toggle-on'
                              }`}
                              onClick={() => handleRuleStatusChange(rule)}
                            >
                              <i className={`fas ${rule.status === 'active' ? 'fa-ban' : 'fa-check'}`} />
                              {rule.status === 'active' ? 'Inactive' : 'Active'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="fm-empty">
                        No fine rules found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showFineModal && (
        <div className="fm-modal-overlay">
          <div className="fm-modal-box">
            <form onSubmit={handleSubmitFine}>
              <div className="fm-modal-head">
                <h5>
                  <i className="fas fa-plus-circle" />
                  Create Fine
                </h5>
                <button
                  type="button"
                  className="fm-modal-close"
                  onClick={closeFineModal}
                  disabled={savingFine}
                >
                  ×
                </button>
              </div>

              <div className="fm-modal-body">
                <div className="fm-form-grid">
                  <div>
                    <label className="fm-form-label">Issue / Book</label>
                    <SearchableSelect
                      name="issue_id"
                      value={fineForm.issue_id}
                      onChange={handleFineChange}
                      options={issues.map((item) => ({
                        value: item.id,
                        label: `${getBookName(item)} - ${getMemberName(item)}${item.membership_no ? ` (${item.membership_no})` : ''}`
                      }))}
                      placeholder="Select Issue"
                    />
                  </div>

                  <div>
                    <label className="fm-form-label">Member *</label>
                    <SearchableSelect
                      name="member_id"
                      value={fineForm.member_id}
                      onChange={handleFineChange}
                      options={members.map((member) => ({
                        value: member.id,
                        label: `${getMemberName(member)}${member.mobile || member.phone ? ` - ${member.mobile || member.phone}` : ''}`
                      }))}
                      placeholder="Select Member"
                      disabled={!!fineForm.issue_id}
                      required
                    />
                  </div>

                  <div>
                    <label className="fm-form-label">Fine Type *</label>
                    <SearchableSelect
                      name="fine_type"
                      value={fineForm.fine_type}
                      onChange={handleFineChange}
                      options={FINE_TYPE_OPTIONS}
                      clearable={false}
                    />
                  </div>

                  <div>
                    <label className="fm-form-label">Membership ID</label>
                    <input
                      type="text"
                      className="fm-input"
                      value={fineForm.membership_id || ''}
                      readOnly
                    />
                  </div>

                  <div>
                    <label className="fm-form-label">Amount *</label>
                    <input
                      type="number"
                      name="amount"
                      className="fm-input"
                      value={fineForm.amount}
                      onChange={handleFineChange}
                      min="1"
                      required
                    />

                    {fineForm.reason === 'No late fine applicable' ? (
                      <div className="fm-auto-note-warning">
                        <i className="fas fa-circle-info me-1" />
                        No late fine applicable. Select different issue/fine type or enter manual amount.
                        {fineForm.auto_note ? ` (${fineForm.auto_note})` : ''}
                      </div>
                    ) : (
                      fineForm.auto_note && (
                        <div className="fm-auto-note">
                          <i className="fas fa-circle-info me-1" />
                          {fineForm.auto_note}
                        </div>
                      )
                    )}
                  </div>

                  <div>
                    <label className="fm-form-label">Status</label>
                    <input
                      type="text"
                      className="fm-input"
                      value="Unpaid"
                      readOnly
                    />
                  </div>

                  <div className="fm-field-full">
                    <label className="fm-form-label">Reason</label>
                    <input
                      type="text"
                      name="reason"
                      className="fm-input"
                      value={fineForm.reason}
                      onChange={handleFineChange}
                      placeholder="Late return / Damage / Lost book"
                    />
                  </div>

                  <div className="fm-field-full">
                    <label className="fm-form-label">Remarks</label>
                    <textarea
                      name="remarks"
                      rows="3"
                      className="fm-textarea"
                      value={fineForm.remarks}
                      onChange={handleFineChange}
                      placeholder="Enter remarks"
                    />
                  </div>
                </div>
              </div>

              <div className="fm-modal-footer">
                <button
                  type="button"
                  className="fm-btn-light"
                  onClick={closeFineModal}
                  disabled={savingFine}
                >
                  Cancel
                </button>

                <button type="submit" className="fm-btn-primary" disabled={savingFine}>
                  {savingFine ? (
                    <>
                      <i className="fas fa-spinner fa-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save" />
                      Save Fine
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRuleModal && (
        <div className="fm-modal-overlay">
          <div className="fm-modal-box">
            <form onSubmit={handleSubmitRule}>
              <div className="fm-modal-head">
                <h5>
                  <i className="fas fa-sliders-h" />
                  {editingRuleId ? 'Update Fine Rule' : 'Create Fine Rule'}
                </h5>
                <button
                  type="button"
                  className="fm-modal-close"
                  onClick={closeRuleModal}
                  disabled={savingRule}
                >
                  ×
                </button>
              </div>

              <div className="fm-modal-body">
                <div className="fm-form-grid">
                  <div>
                    <label className="fm-form-label">Member Source *</label>
                    <SearchableSelect
                      name="member_source"
                      value={ruleForm.member_source}
                      onChange={handleRuleChange}
                      options={RULE_MEMBER_SOURCE_OPTIONS}
                      required
                      clearable={false}
                    />
                  </div>

                  <div>
                    <label className="fm-form-label">Late Fine Per Day *</label>
                    <input
                      type="number"
                      name="late_fine_per_day"
                      className="fm-input"
                      value={ruleForm.late_fine_per_day}
                      onChange={handleRuleChange}
                      min="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="fm-form-label">Grace Days</label>
                    <input
                      type="number"
                      name="grace_days"
                      className="fm-input"
                      value={ruleForm.grace_days}
                      onChange={handleRuleChange}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="fm-form-label">Lost Charge Type</label>
                    <SearchableSelect
                      name="lost_book_charge_type"
                      value={ruleForm.lost_book_charge_type}
                      onChange={handleRuleChange}
                      options={CHARGE_TYPE_OPTIONS}
                      clearable={false}
                    />
                  </div>

                  <div>
                    <label className="fm-form-label">Lost Charge</label>
                    <input
                      type="number"
                      name="lost_book_charge"
                      className="fm-input"
                      value={ruleForm.lost_book_charge}
                      onChange={handleRuleChange}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="fm-form-label">Damage Charge Type</label>
                    <SearchableSelect
                      name="damaged_book_charge_type"
                      value={ruleForm.damaged_book_charge_type}
                      onChange={handleRuleChange}
                      options={CHARGE_TYPE_OPTIONS}
                      clearable={false}
                    />
                  </div>

                  <div>
                    <label className="fm-form-label">Damage Charge</label>
                    <input
                      type="number"
                      name="damaged_book_charge"
                      className="fm-input"
                      value={ruleForm.damaged_book_charge}
                      onChange={handleRuleChange}
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="fm-form-label">Status</label>
                    <SearchableSelect
                      name="status"
                      value={ruleForm.status}
                      onChange={handleRuleChange}
                      options={RULE_STATUS_OPTIONS}
                      clearable={false}
                    />
                  </div>
                </div>
              </div>

              <div className="fm-modal-footer">
                <button
                  type="button"
                  className="fm-btn-light"
                  onClick={closeRuleModal}
                  disabled={savingRule}
                >
                  Cancel
                </button>

                <button type="submit" className="fm-btn-primary" disabled={savingRule}>
                  {savingRule ? (
                    <>
                      <i className="fas fa-spinner fa-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save" />
                      {editingRuleId ? 'Update Rule' : 'Save Rule'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FineList;
